<template>
  <div class="account-page scrollable">
    <div class="page-header">
      <h1>Proxy List</h1>
      <el-breadcrumb separator="/">
        <el-breadcrumb-item :to="{ path: '/' }">
          <i class="mdi mdi-home-outline"></i>
        </el-breadcrumb-item>
        <el-breadcrumb-item>Proxy Table</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div class="top-bar">
      <div class="count-user">
        <p>Total Proxy Count: {{ totalProxies }}</p>
      </div>

      <el-form inline>
      <el-form-item>
        <el-input
          v-model="searchQuery"
          placeholder="Search by IP or City"
          :style="{ 'width': '400px' }"
          @keyup.enter="searchProxies"
        ></el-input>
      </el-form-item>

      <el-form-item>
        <el-button v-if="isAdmin" type="info" @click="searchProxies">
          <i class="mdi mdi-magnify"></i> Find Proxy
        </el-button>
      </el-form-item>
    </el-form>
    </div>

    <div class="top-bar">
      <div class="accounts-filter">
        Show
        <el-select
            v-model="perPage"
            @change="changeCount"
            placeholder=" "
            style="width:70px"
        >
          <el-option
              v-for="item in countProxyOptions"
              :key="item.value"
              :label="item.value"
              :value="item.value"
          >
          </el-option>
        </el-select>
        entries
      </div>

      <div class="header-buttons">
        <el-button v-if="isAdmin"
                    type="primary"
                    @click="goToAddProxy"
        >+ Add Proxy
        </el-button>          
      </div>      
    </div> 

    <el-table
      ref="indexingTable"
      :data="proxies"
      style="width: 100%;"
      @sort-change="sort"
      v-loading="loading"
      element-loading-text="Loading proxies..."
      element-loading-spinner="el-icon-loading">

      <el-table-column prop="id" label="ID" :min-width="80" sortable>
        <template slot-scope="scope">
          <div :style="{ textAlign: 'center' }">{{ scope.row.id }}</div>
        </template>
      </el-table-column>

      <el-table-column prop="username" label="Username" :min-width="130">
        <template slot-scope="scope">
          <div :style="{ textAlign: 'center' }">{{ scope.row.username }}</div>
        </template>
      </el-table-column>

      <el-table-column prop="ip" label="IP Address" :min-width="150" sortable>
        <template slot-scope="scope">
          <div :style="{ textAlign: 'center' }">{{ scope.row.ip }}</div>
        </template>
      </el-table-column>

      <el-table-column prop="port" label="Port" :min-width="100" sortable>
        <template slot-scope="scope">
          <div :style="{ textAlign: 'center' }">{{ scope.row.port }}</div>
        </template>
      </el-table-column>  
      
      <el-table-column prop="city" label="City" :min-width="130" sortable>
        <template slot-scope="scope">
          <div :style="{ textAlign: 'center' }">{{ scope.row.city || 'N/A' }}</div>
        </template>
      </el-table-column> 
      
      <el-table-column prop="created_at" label="Created" :min-width="130" sortable>
        <template slot-scope="scope">
          <div :style="{ textAlign: 'center' }">{{ formatDate(scope.row.created_at) }}</div>
        </template>
      </el-table-column>

      <!-- actions -->
      <el-table-column label="Operations" width="260px">
        <template slot-scope="scope">
          <el-button v-if="isAdmin"
                     size="mini"
                     type="info"
                     @click="handleEdit(scope.$index, scope.row)"
          ><i class="mdi mdi-pencil"></i> Edit 
          </el-button>          
          <el-button v-if="isAdmin"
                     size="mini"
                     type="danger"
                     @click="handleDelete(scope.row)"
          ><i class="mdi mdi-delete"></i> Delete
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagin-wrp">
      <el-pagination
          :background="true"
          layout="prev, pager, next"
          :page-size="perPage"
          :total="totalProxies"
          :current-page="currentPage"
          @current-change="choosePage"
      >
      </el-pagination>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getProxyPaidList, deleteProxyPaid } from '@/api/proxyApi'

export default {
  name: 'ProxyTable',
  data () {
    return {
      proxies: [],
      loading: false,
      searchQuery: '',
      currentPage: 1,
      perPage: 10,
      totalProxies: 0,
      sortBy: {
        id: 0,
        ip: 0,
        port: 0,
        city: 0,
        created_at: 0,
      },
      countProxyOptions: [
        {value: 10},
        {value: 25},
        {value: 50},
        {value: 100},
      ],
    }
  },
  computed: {
    ...mapGetters({
      user: 'userData'
    }),

    isAdmin() {
      return this.user ? this.user.is_admin : false;
    },
  },
  mounted () {
    this.loadProxies()
  },
  methods: {
    async loadProxies() {
      this.loading = true
      try {
        const params = this.getRequestParams()
        const response = await getProxyPaidList(params)

        if (response && response.data) {
          this.proxies = response.data
          this.totalProxies = response.meta ? response.meta.total : (response.total || response.data.length)
          this.currentPage = response.meta ? response.meta.current_page : (response.current_page || 1)
        } else if (Array.isArray(response)) {
          // Fallback if response is directly an array
          this.proxies = response
          this.totalProxies = response.length
          this.currentPage = 1
        } else {
          console.warn('Unexpected response structure:', response)
          this.proxies = []
          this.totalProxies = 0
          this.currentPage = 1
        }
      } catch (error) {
        console.error('Error loading proxies:', error.response || error.message || error)
        this.$notify.error({
          title: 'Error',
          message: 'Failed to load proxies'
        })
        this.proxies = []
        this.totalProxies = 0
      } finally {
        this.loading = false
      }
    },

    getRequestParams() {
      const params = {
        page: this.currentPage,
        per_page: this.perPage
      }

      if (this.searchQuery && this.searchQuery.trim()) {
        params.search = this.searchQuery.trim()
      }

      return params
    },

    searchProxies() {
      this.currentPage = 1
      this.loadProxies()
    },

    choosePage(page) {
      this.currentPage = page
      this.loadProxies()
    },

    sort(sortData) {
      // Handle sorting if needed
      console.log('Sort:', sortData)
    },

    changeCount(count) {
      this.perPage = count
      this.currentPage = 1
      this.loadProxies()
    },

    goToAddProxy() {
      this.$router.push({name: 'proxy-create'})
    },

    handleEdit(index, row) {
      this.$router.push({name: 'proxy-edit', params: {id: row.id}})
    },

    async handleDelete(proxy) {
      try {
        await this.$confirm(`Are you sure you want to delete proxy ${proxy.ip}:${proxy.port}?`, 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        })

        await deleteProxyPaid(proxy.id)

        this.$notify.success({
          title: 'Success',
          message: 'Proxy deleted successfully'
        })

        this.loadProxies()

      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error deleting proxy:', error)
          this.$notify.error({
            title: 'Error',
            message: 'Failed to delete proxy'
          })
        }
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleString()
    }
  }
}
</script>

<style scoped lang="scss">

.account-page {
  overflow-x: hidden;
}
th {
  .cell {
    .pointer {
      cursor: pointer;
    }
  }
}

.accounts-filter {
  width: 200px;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  & > div {
    margin: 0 5px;
  }
}
.selected-row {
  background-color: red !important;
}
</style>
<style lang="scss">
.selected-row {
  background-color: #d8e2e942 !important;
}
</style>
